import { useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, Loader2, CheckCircle2, AlertCircle, Images, FolderInput } from "lucide-react";

/**
 * Bulk image uploader.
 * - Local: multi-select file picker → /api/uploads?watermark=true → /api/media
 * - Drive: paste any number of public Drive URLs / IDs → /api/media/import-from-drive
 *   (server fetches, watermarks, stores, creates media records in one shot)
 * media_url is stored as a RELATIVE path so the frontend always resolves against
 * its own REACT_APP_BACKEND_URL — records work identically on preview and production.
 */
export default function BulkImageUploader({
  championshipId = null,
  defaultTitle = "",
  onDone,
  testid = "bulk-image-uploader",
}) {
  const inputRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const [showDrive, setShowDrive] = useState(false);
  const [driveUrls, setDriveUrls] = useState("");
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveResult, setDriveResult] = useState(null); // {imported, failed, failures[]}
  const [driveProgress, setDriveProgress] = useState(null); // {current, total}


  const pick = () => inputRef.current?.click();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const initial = files.map((f) => ({ name: f.name, status: "queued", url: "", error: "" }));
    setQueue(initial);

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setQueue((q) => q.map((item, idx) => (idx === i ? { ...item, status: "uploading" } : item)));
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data: up } = await api.post("/uploads?watermark=true", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // Store as a RELATIVE path (frontend resolves via REACT_APP_BACKEND_URL).
        const relativeUrl = up.url;
        const mediaBody = {
          title: defaultTitle || (f.name || "").replace(/\.[a-z0-9]+$/i, ""),
          category: "photo",
          media_url: relativeUrl,
          priority: 0,
        };
        if (championshipId) mediaBody.championship_id = championshipId;
        await api.post("/media", mediaBody);
        setQueue((q) => q.map((item, idx) => (idx === i ? { ...item, status: "done", url: relativeUrl } : item)));
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Upload failed";
        setQueue((q) => q.map((item, idx) => (idx === i ? { ...item, status: "error", error: String(detail) } : item)));
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    if (onDone) onDone();
  };

  const importFromDrive = async () => {
    if (!driveUrls.trim()) return;
    setDriveBusy(true);
    setDriveResult(null);
    setDriveProgress({ current: 0, total: 0 });
    try {
      const body = { urls: driveUrls, watermark: true };
      if (championshipId) body.championship_id = championshipId;
      const { data: kickoff } = await api.post("/media/import-from-drive", body);
      const jobId = kickoff.job_id;
      if (!jobId) throw new Error("Failed to start import job");
      // Poll every 2.5 s until done/failed/cancelled/interrupted.
      // A single import can run for tens of minutes for a large folder; we keep polling.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2500));
        let job;
        try {
          const { data } = await api.get(`/media/import-jobs/${jobId}`);
          job = data;
        } catch {
          continue; // transient network hiccup — try again
        }
        setDriveProgress({ current: job.processed || 0, total: job.total || 0 });
        setDriveResult({
          imported: job.imported || 0,
          failed: job.failed || 0,
          failures: job.failures || [],
          status: job.status,
          message: job.message,
        });
        if (job.imported > 0 && onDone) onDone();
        if (["done", "failed", "cancelled", "interrupted"].includes(job.status)) {
          if (job.status === "done" && job.failed === 0 && job.imported > 0) setDriveUrls("");
          break;
        }
      }
    } catch (err) {
      setDriveResult({
        imported: 0,
        failed: 1,
        failures: [{ error: err.response?.data?.detail || err.message || "Import failed" }],
        status: "failed",
      });
    } finally {
      setDriveBusy(false);
      if (onDone) onDone();
    }
  };

  const doneCount = queue.filter((q) => q.status === "done").length;
  const errorCount = queue.filter((q) => q.status === "error").length;

  return (
    <div data-testid={testid} className="border border-dashed border-slate-300 rounded-md p-4 bg-slate-50/60">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={pick}
          data-testid={`${testid}-pick`}
          className="inline-flex items-center gap-2 bg-nkf-blue hover:bg-nkf-navy text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded transition disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Images className="h-4 w-4" />}
          {busy ? "Uploading…" : "Bulk Upload Photos"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setShowDrive((v) => !v)}
          data-testid={`${testid}-toggle-drive`}
          className="inline-flex items-center gap-2 border border-nkf-blue text-nkf-blue hover:bg-nkf-blue hover:text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded transition disabled:opacity-60"
        >
          <FolderInput className="h-4 w-4" /> Import from Drive
        </button>
        <span className="text-xs text-slate-500">
          Each image is auto-watermarked with the NKF & UKF logos.
        </span>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFiles}
          data-testid={`${testid}-input`}
        />
      </div>

      {showDrive && (
        <div className="mt-4 bg-white border border-slate-200 rounded-md p-3.5" data-testid={`${testid}-drive-panel`}>
          <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold mb-2 flex items-center gap-1.5">
            <FolderInput className="h-3.5 w-3.5" /> Google Drive import
          </div>
          <p className="text-xs text-slate-500 mb-2">
            Paste one or more Google Drive image URLs <strong>or a folder URL</strong> (one per line). Files/folders must be shared as
            <strong> Anyone with the link</strong>. Photos are copied into NKF storage and watermarked — originals stay in your Drive.
          </p>
          <textarea
            value={driveUrls}
            onChange={(e) => setDriveUrls(e.target.value)}
            placeholder="https://drive.google.com/drive/folders/FOLDER_ID&#10;https://drive.google.com/file/d/FILE_ID/view"
            rows={5}
            data-testid={`${testid}-drive-urls`}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-xs font-mono resize-y"
          />
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              disabled={driveBusy || !driveUrls.trim()}
              onClick={importFromDrive}
              data-testid={`${testid}-drive-import`}
              className="inline-flex items-center gap-2 bg-nkf-red hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded transition disabled:opacity-60"
            >
              {driveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderInput className="h-4 w-4" />}
              {driveBusy ? "Importing…" : "Import Now"}
            </button>
            {driveResult && (
              <div className="text-xs flex flex-wrap items-center gap-3">
                {driveResult.imported > 0 && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {driveResult.imported} imported
                  </span>
                )}
                {driveResult.failed > 0 && (
                  <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                    <AlertCircle className="h-3.5 w-3.5" /> {driveResult.failed} failed
                  </span>
                )}
              </div>
            )}
          </div>
          {driveBusy && driveProgress && (
            <div className="mt-3" data-testid={`${testid}-drive-progress`}>
              <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-slate-500 mb-1">
                <span>
                  {driveProgress.total > 0
                    ? `Processing ${driveProgress.current} of ${driveProgress.total}`
                    : "Resolving folder…"}
                </span>
                {driveProgress.total > 0 && (
                  <span>{Math.round((driveProgress.current / driveProgress.total) * 100)}%</span>
                )}
              </div>
              <div className="h-1.5 bg-slate-200 rounded overflow-hidden">
                <div
                  className={`h-full bg-nkf-blue transition-all ${driveProgress.total === 0 ? "animate-pulse w-1/3" : ""}`}
                  style={driveProgress.total > 0 ? { width: `${(driveProgress.current / driveProgress.total) * 100}%` } : undefined}
                />
              </div>
              {driveResult && driveResult.message && (
                <div className="mt-1 text-[11px] text-slate-500">{driveResult.message}</div>
              )}
            </div>
          )}
          {driveResult && driveResult.failures && driveResult.failures.length > 0 && (
            <ul className="mt-2 max-h-32 overflow-y-auto text-xs divide-y divide-slate-200 border border-red-100 rounded bg-red-50/40" data-testid={`${testid}-drive-failures`}>
              {driveResult.failures.map((f, i) => (
                <li key={i} className="px-3 py-1.5 flex items-start gap-2 text-red-700">
                  <AlertCircle className="h-3 w-3 mt-0.5" />
                  <span className="truncate flex-1">{f.file_id ? `${f.file_id}: ` : ""}{f.error}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {queue.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-3 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> {doneCount} done</span>
            {errorCount > 0 && <span className="inline-flex items-center gap-1 text-red-600"><AlertCircle className="h-3.5 w-3.5" /> {errorCount} failed</span>}
            <span className="text-slate-400">{queue.length} total</span>
          </div>
          <ul className="mt-2 max-h-32 overflow-y-auto text-xs divide-y divide-slate-200 border border-slate-200 rounded bg-white">
            {queue.map((q, i) => (
              <li key={i} className="px-3 py-1.5 flex items-center gap-2">
                {q.status === "queued"    && <Upload   className="h-3 w-3 text-slate-400" />}
                {q.status === "uploading" && <Loader2  className="h-3 w-3 text-nkf-blue animate-spin" />}
                {q.status === "done"      && <CheckCircle2 className="h-3 w-3 text-emerald-600" />}
                {q.status === "error"     && <AlertCircle  className="h-3 w-3 text-red-600" />}
                <span className="truncate flex-1">{q.name}</span>
                {q.status === "error" && <span className="text-red-600 truncate max-w-[45%]" title={q.error}>{q.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
